import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import Auth from '../../../api/auth';
import Table, { type TableProps } from '../../../components/Table';
import useApiQuery from '../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { Organization } from '@/types/organization';
import type { TableRowData } from '@/types/tableTypes';

const ONE_YEAR_IN_MS = 365 * 24 * 3600 * 1000;

interface MarketplaceItem {
  _id: string;
  name: string;
  id: string;
}

interface MarketplaceItemsProps {
  organization: Organization;
  setOrganization: (org: Organization) => void;
  canActivate: boolean;
}

const MarketplaceItems = ({
  organization,
  setOrganization,
  canActivate,
}: MarketplaceItemsProps): React.ReactElement => {
  const intl = useIntl();
  const {
    data: marketplaceItems,
    error: fetchError,
    makeApiCall: getMarketplaceItems,
  } = useApiQuery<MarketplaceItem[]>(Auth.fetchV1MarketplaceItems);
  const { makeApiCall: activateMarketplaceItem } = useApiQuery(
    Auth.activateMarketplaceItem as unknown as APIHookArg
  );

  useEffect(() => {
    getMarketplaceItems();
  }, [getMarketplaceItems]);

  const isOrgOlderThanOneYear = (): boolean =>
    organization.createdAt
      ? new Date().getTime() - new Date(organization.createdAt).getTime() > ONE_YEAR_IN_MS
      : false;

  const activateItem = async (item: { id: string; name: string }): Promise<void> => {
    const data = await activateMarketplaceItem({
      _id: organization._id,
      marketplaceItemId: item.id,
    });
    if (data && typeof data === 'object' && '_id' in data) {
      setOrganization(data as Organization);
    }
  };

  const fields = [
    {
      id: 'name',
      title: { id: 'name' },
    },
    {
      id: 'status',
      title: { id: 'status' },
      type: 'options',
    },
  ];

  const rows: TableRowData[] = (marketplaceItems ?? [])
    .filter(({ name }: MarketplaceItem) => name !== 'Monitoring' && name !== 'Safescarf')
    .map(({ _id, name }: MarketplaceItem) => {
      const activated = organization.marketplaceItemIds?.includes(_id);

      const isItemDisabled = (name: string, activated: boolean): boolean => {
        // Early returns for clarity
        if (!canActivate) return true;
        if (activated) return true;
        if (name === 'Evaluation' && isOrgOlderThanOneYear()) return true;

        return false;
      };

      return {
        key: _id,
        data: [
          {
            field: 'name',
            value: name,
          },
          {
            field: 'status',
            type: 'switch',
            value: intl.formatMessage({ id: activated ? 'active' : 'inactive' }),
            props: {
              disabled: isItemDisabled(name, activated ?? false),
              checked: activated,
              name,
              label: intl.formatMessage({ id: activated ? 'active' : 'inactive' }),
              onChange: () => !activated && activateItem({ id: _id, name }),
            },
          },
        ],
      };
    });

  return (
    <Table
      header={intl.formatMessage({ id: 'marketplace' })}
      fields={fields}
      rows={rows}
      error={(fetchError as TableProps['error']) ?? ''}
    />
  );
};

export default MarketplaceItems;
